import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';
import { formatCurrency, formatDateTime, trimToCents } from '../utils/format';

/**
 * Closeout queue.
 *
 * Settlement packets waiting on approval, and the record of the ones already
 * decided. The approver reads the stored letter here rather than a
 * regeneration: the bytes served are the bytes that were hashed at
 * submission, so approving binds to a specific document.
 *
 * An approver does not only read the sheet, they edit it. Describing a fix is
 * slower and less reliable than making it, and the people who approve are the
 * people who know the figures. Every edit is recorded as was/now against their
 * name and shown to the preparer, the letter is rebuilt and rehashed, and the
 * recorded views are cleared — so Approve re-arms only once somebody has read
 * the letter that now exists.
 *
 * Must-fix findings are overridable by an approver with a reason, because a
 * distribution is ultimately a management call. Three are not, because there
 * is no decision in them, only a correction; the server decides which is
 * which and says so per finding.
 */

/**
 * Two tabs: work that is still live, and work that is finished with.
 *
 * A sent-back packet used to land in "Decided", beside the approved and the
 * withdrawn ones. It is the opposite of decided — it is the one state where
 * somebody still has to do something — so the only thing actually telling the
 * preparer was a Slack message. Miss that, and the work stopped with nothing
 * on screen saying so.
 *
 * It sits with the pending ones instead. Both are open: what differs is who
 * is holding them, and the pill on each row says which. Hence "Open" rather
 * than "Awaiting approval", because half of these are not waiting on an
 * approver at all.
 */
const TABS = {
  open: {
    label: 'Open',
    states: 'pending,changes_requested',
    counts: ['pending', 'changes_requested'],
  },
  approved: { label: 'Approved', states: 'approved' },
};

// Withdrawn and superseded packets are not approved, and a tab headed
// "Approved" must not quietly contain them — these are the records of who
// released money on which document. They are also not work: nobody browses
// for them, they are looked up when a question comes back months later. So
// they sit behind a toggle on the Approved tab rather than taking a tab of
// their own, or being folded in under a heading that would be untrue.
const ARCHIVED_STATES = 'withdrawn,superseded';

// Mirrors LIVE_APPROVAL_STATES on the server. Every control below gates on
// this rather than on 'pending', which is what left a sent-back packet
// sitting in Open with no button on it that would do anything — the API had
// already been widened to accept it.
const LIVE_STATES = TABS.open.states.split(',');
const isLive = (state) => LIVE_STATES.includes(state);

const DETAIL_FIELDS = [
  ['client_name', 'Client first name'],
  ['client_last_name', 'Client surname'],
  ['client_title', 'Title'],
  ['client_cell', 'Cell'],
  ['client_email', 'Email'],
  ['matter_description', 'RE line'],
  ['date_of_loss', 'Date of loss'],
];

const num = (v) => {
  const n = parseFloat(String(v ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

/** The sheet as the approver is currently editing it, seeded from the packet. */
function seedFrom(row) {
  const p = row.payload || {};
  return {
    ...p,
    settlement_amount: String(p.settlement_amount ?? ''),
    attorney_fees: trimToCents(p.attorney_fees),
    costs: String(p.costs ?? ''),
    client_address_lines: (p.client_address_lines || []).join('\n'),
    liens: (p.liens || []).map((row) => ({
      name: row[0],
      amount: String(row[1]),
      // What this party originally billed or asserted, which prints in
      // brackets on the client's letter. Blank is normal.
      original: row[2] == null ? '' : String(row[2]),
      original_kind: row[3] || 'billed',
    })),
    // The three breakdowns, as editable rows rather than the pairs they
    // arrive and leave as. Amounts stay strings so a half-typed figure is
    // not read as a number mid-keystroke, exactly as the liens are.
    settlement_sources: pairsIn(p.settlement_sources),
    fee_splits: pairsIn(p.fee_splits),
    advances: pairsIn(p.advances),
  };
}

const pairsIn = (rows) =>
  (rows || []).map((r) => ({ name: r[0] ?? '', amount: String(r[1] ?? '') }));

const pairsOut = (rows) =>
  (rows || [])
    .filter((r) => String(r.name || '').trim() && num(r.amount) !== 0)
    .map((r) => [String(r.name).trim(), num(r.amount)]);

/** Back to the shape the API takes. */
function toPayload(sheet, row, overrides) {
  return {
    ...sheet,
    settlement_amount: num(sheet.settlement_amount),
    attorney_fees: num(sheet.attorney_fees),
    costs: num(sheet.costs),
    client_address_lines: String(sheet.client_address_lines || '')
      .split('\n').filter(Boolean),
    liens: (sheet.liens || [])
      .filter((l) => String(l.name).trim())
      .map((l) => [
        l.name, num(l.amount),
        l.original === '' || l.original == null ? null : num(l.original),
        l.original_kind || 'billed',
      ]),
    settlement_sources: pairsOut(sheet.settlement_sources),
    fee_splits: pairsOut(sheet.fee_splits),
    advances: pairsOut(sheet.advances),
    acknowledged: row.acknowledged || [],
    acknowledgement_note: row.acknowledgement_note || null,
    overrides,
  };
}

/**
 * Everything on the sheet that reaches the letter, in a fixed order.
 *
 * Used to tell whether an approver has typed something they have not saved.
 * A decision posts only the decision and the note — the figures travel
 * through Save, which rebuilds the letter. Nothing stopped an approver from
 * editing the amounts and then pressing Request changes or Approve without
 * saving, and the edits lived in this component and died there: the preparer
 * got a note describing changes to figures that had never moved, and an
 * approval signed off the letter built from the preparer's own originals.
 *
 * Reported from the liens department on 2026-11-02, and again after the
 * deploy when it was still happening.
 */
function sheetPrint(s) {
  if (!s) return '';
  return JSON.stringify([
    s.settlement_amount, s.attorney_fees, s.costs, s.fee_label,
    s.client_address_lines, s.released_parties, s.delivery_method,
    DETAIL_FIELDS.map(([k]) => s[k] ?? ''),
    (s.liens || []).map((l) => [l.name, l.amount, l.original, l.original_kind]),
    ['settlement_sources', 'fee_splits', 'advances'].map(
      (k) => (s[k] || []).map((r) => [r.name, r.amount])),
  ]);
}

/**
 * The figure as it stood before whoever last touched the sheet moved it.
 *
 * The preparer's form marks a changed figure where it sits; so does this
 * one. A list at the top says two figures moved, and on a sheet with
 * fifteen boxes on it that does not say which two.
 */
function Was({ change }) {
  if (!change) return null;
  return (
    <div style={s.wasLine}>
      {change.now == null
        ? 'taken off the sheet'
        : <>was <s>{change.was_text}</s></>}
    </div>
  );
}

/** What changed on this packet, looked up by the figure it belongs to. */
function changeLookup(row) {
  const byField = {};
  const byRow = {};
  (row.changes || []).forEach((c) => {
    if (c.name) byRow[String(c.name).trim().toLowerCase()] = c;
    else if (c.field) byField[c.field] = c;
  });
  return {
    field: (f) => byField[f],
    row: (n) => byRow[String(n || '').trim().toLowerCase()],
  };
}

/** Rows of a breakdown as the approver is editing them. */
function breakdown(sheet, key) {
  return sheet[key] || [];
}

function breakdownTotal(sheet, key) {
  return breakdown(sheet, key).reduce((t, r) => t + num(r.amount), 0);
}

/**
 * The two rules the letter builder enforces, checked here so an approver
 * reads them beside the figures instead of as a 422 after pressing Save.
 * The builder keeps its copy: this one is a courtesy, that one is the
 * guarantee.
 */
function sheetErrors(sheet) {
  const out = [];
  const settlement = num(sheet.settlement_amount);
  const sources = breakdown(sheet, 'settlement_sources')
    .filter((r) => String(r.name || '').trim());
  if (sources.length > 0) {
    const total = sources.reduce((t, r) => t + num(r.amount), 0);
    if (Math.abs(total - settlement) > 0.005) {
      out.push(`The settlement sources add up to ${formatCurrency(total)}, but `
        + `the settlement is ${formatCurrency(settlement)}. They are the same `
        + 'money broken out.');
    }
  }
  const splits = breakdownTotal(sheet, 'fee_splits');
  if (splits > num(sheet.attorney_fees) + 0.005) {
    out.push(`The fee split adds up to ${formatCurrency(splits)}, more than the `
      + `${formatCurrency(num(sheet.attorney_fees))} in attorney fees. A `
      + 'division comes out of the fee.');
  }
  if (netOf(sheet) < 0) {
    out.push('Payments and loans come to more than the settlement, so this '
      + 'sheet leaves the client with nothing.');
  }
  return out;
}

function liensOf(sheet) {
  return (sheet.liens || []).reduce((t, l) => t + num(l.amount), 0);
}

/** What is left before anything comes out of the client's own share. */
function grossOf(sheet) {
  return num(sheet.settlement_amount) - num(sheet.attorney_fees)
    - num(sheet.costs) - liensOf(sheet);
}

/**
 * Live net, so the approver sees the consequence of a change as they type.
 *
 * Loans and advances are subtracted here because the letter subtracts them.
 * Without that the approver signs off on a figure the client's own letter
 * does not carry — and it is the larger of the two, which is the wrong way
 * for that error to run.
 */
function netOf(sheet) {
  return grossOf(sheet) - breakdownTotal(sheet, 'advances');
}

function CloseoutApprovals() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({
    can_approve: false, approvers: [], slack_configured: true, counts: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('open');
  const [open, setOpen] = useState(null);     // approval id expanded
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  // The open packet's sheet as this approver is editing it, the reasons they
  // have given for each must-fix they are clearing, and what the server last
  // refused. Keyed to nothing: only one packet is open at a time, and seeding
  // from the row on expand is what keeps an edit from outliving its packet.
  const [sheet, setSheet] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [blocked, setBlocked] = useState(null);
  const [problem, setProblem] = useState('');
  // Withdrawn and superseded packets, off by default. See ARCHIVED_STATES.
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const state = tab === 'approved' && showArchived
        ? `${TABS.approved.states},${ARCHIVED_STATES}`
        : TABS[tab].states;
      const r = await axios.get(`${API_URL}/api/settlement-approvals?state=${state}`,
        { withCredentials: true });
      setRows(r.data.approvals || []);
      setMeta({
        can_approve: !!r.data.can_approve,
        approvers: r.data.approvers || [],
        slack_configured: !!r.data.slack_configured,
        counts: r.data.counts || {},
      });
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [tab, showArchived]);

  useEffect(() => { load(); }, [load]);

  // Deep link from the Slack message: ?closeout=<id> opens that packet.
  //
  // The id alone is not enough. This only set which packet to expand, while
  // the queue went on loading whichever tab was selected — pending, by
  // default. The moment a packet was decided it left that tab, so following
  // the link from a "changes requested" message landed on "Nothing is waiting
  // on approval": the packet was not in the list to expand, and the preparer
  // had no way to reach the note explaining what to change. Which is the one
  // thing that message exists to deliver.
  //
  // So ask the server what state it is in first, and open the tab that holds
  // it. One extra request on a link that is followed once.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('closeout');
    if (!id) return undefined;
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/settlement-approvals/${id}`,
          { withCredentials: true });
        if (!alive) return;
        if (['pending', 'changes_requested'].includes(r.data.state)) {
          setTab('open');
        } else {
          // A link to a withdrawn or superseded packet has to turn the
          // archive on, or it lands on a list that does not contain it.
          setTab('approved');
          if (r.data.state !== 'approved') setShowArchived(true);
        }
        setOpen(id);
      } catch (e) {
        if (!alive) return;
        // Withdrawn, deleted, or someone else's. Say so — an empty queue
        // looks identical to a broken link.
        setError(e.response?.status === 404
          ? 'That closeout packet no longer exists. It may have been withdrawn.'
          : (e.response?.data?.detail || e.message));
      }
    })();
    return () => { alive = false; };
  }, []);

  // Expanding a packet seeds the editor from what the server holds, and
  // reseeds only when the packet itself moves on — a new id, or a revision
  // somebody else saved.
  //
  // Keyed on that rather than on `rows`, which changes identity on every
  // reload. Opening the letter reloads the queue (that is what records the
  // view and arms Approve), so reseeding on `rows` threw away whatever the
  // approver had typed the moment they went to read the letter — the exact
  // lost-work bug the saved draft was added to fix on the other side.
  const openRow = rows.find((r) => r.id === open) || null;
  const seedKey = openRow ? `${openRow.id}:${openRow.revision}` : '';
  useEffect(() => {
    const row = rows.find((r) => r.id === open);
    setSheet(row ? seedFrom(row) : null);
    setOverrides({});
    setBlocked(null);
    setNote('');
    setProblem('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey]);

  const fail = (e) => {
    const d = e.response?.data?.detail;
    if (d && typeof d === 'object') {
      setBlocked(d.reconciliation || null);
      setError(d.message || 'That did not go through.');
    } else {
      setBlocked(null);
      setError(d || e.message);
    }
  };

  const download = async (row, final) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/settlement-approvals/${row.id}/document${final ? '?final=true' : ''}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Settlement Letter${final ? '' : ' DRAFT'}_${row.client_name}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      await load();   // the view is now on record, which arms Approve
    } catch (e) {
      let msg = e.message;
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) { /* keep */ }
      }
      setError(msg);
    }
  };

  const save = async (row) => {
    setBusy(true);
    setError(null);
    setBlocked(null);
    try {
      await axios.post(`${API_URL}/api/settlement-approvals/${row.id}/revise`,
        toPayload(sheet, row, overrides), { withCredentials: true });
      await load();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const decide = async (row, decision) => {
    setBusy(true);
    setError(null);
    setBlocked(null);
    try {
      // The sheet travels WITH the decision. It used to travel separately,
      // through Save, and an approver who typed new figures and pressed
      // Request changes sent a note about corrections that never left the
      // browser. Editing and deciding is one action now; the server saves
      // first and decides second, so the figures survive even when the
      // decision is then refused.
      await axios.post(`${API_URL}/api/settlement-approvals/${row.id}/decide`,
        { decision, note: note || null,
          sheet: sheet ? toPayload(sheet, row, overrides) : null },
        { withCredentials: true });
      setNote('');
      setOpen(null);
      await load();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const reopen = async (row) => {
    if (!note.trim()) {
      setError('Say what is wrong with it before reopening it.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/api/settlement-approvals/${row.id}/reopen`,
        { note }, { withCredentials: true });
      setNote('');
      await load();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const raise = async (row) => {
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/api/settlement-approvals/${row.id}/raise`,
        { note: problem }, { withCredentials: true });
      setProblem('');
      await load();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (row) => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/settlement-approvals/${row.id}/withdraw`,
        {}, { withCredentials: true });
      await load();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const stateTag = (st) => {
    const map = {
      pending: ['#714712', '#f5e9cf', '#d5b87f', 'Awaiting approval'],
      approved: ['#36531e', '#e6edcf', '#a5b780', 'Approved'],
      changes_requested: ['#8c3315', '#f8e3dc', '#d39e8a', 'Changes requested'],
      withdrawn: ['#4d5a48', '#eef1e4', '#c3ccb6', 'Withdrawn'],
      superseded: ['#4d5a48', '#eef1e4', '#c3ccb6', 'Superseded'],
    };
    const [color, background, borderColor, label] =
      map[st] || ['#4d5a48', '#eef1e4', '#c3ccb6', st];
    return <span style={{ ...s.tag, color, background, borderColor }}>{label}</span>;
  };

  return (
    <div style={s.wrap}>
      <div className="lm-eyebrow">The final review</div>
      <h1>Closeout</h1>
      <p className="lm-subtitle">Every figure accounted for. Every letter ready for review.</p>

      <div className="lm-tabs" role="tablist" aria-label="Closeout queue">
        {Object.entries(TABS).map(([key, cfg]) => {
          const n = (cfg.counts || []).reduce(
            (t, c) => t + (meta.counts?.[c] || 0), 0);
          return (
            <button key={key} type="button" role="tab"
                    aria-selected={tab === key}
                    onClick={() => setTab(key)}>
              {cfg.label}
              {n > 0 && (
                <span className={`lm-count${meta.counts?.changes_requested ? ' lm-count-loud' : ''}`}>
                  {n}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {!meta.can_approve && (
        <div style={s.note}>
          You can see the queue but not approve or edit from it. Those are limited to{' '}
          {meta.approvers.length ? meta.approvers.join(', ') : 'the configured approvers'}.
        </div>
      )}
      {!meta.slack_configured && (
        <div style={s.warn}>
          Slack is not configured, so packets are not being posted to the closeout
          channel. The queue below still works — approvers have to come here directly.
        </div>
      )}
      {error && <div style={s.err}>{String(error)}</div>}
      {blocked && (blocked.blocking || []).length > 0 && (
        <div style={s.err}>
          <ul style={s.ul}>
            {blocked.blocking.map((f, i) => (
              <li key={`${f.code}:${i}`}>{f.message}</li>
            ))}
          </ul>
        </div>
      )}

      {loading ? (
        <div style={s.muted}>Loading…</div>
      ) : open && !rows.some((r) => r.id === open) ? (
        <div style={s.note}>
          That packet is not in this list. It may be older than the hundred
          shown here — switch tabs, or search the case in Closeout.
        </div>
      ) : rows.length === 0 ? (
        <div style={s.muted}>
          {tab === 'open'
            ? 'Nothing is open — nothing waiting on approval, nothing sent back.'
            : showArchived
            ? 'Nothing approved, withdrawn or superseded yet.'
            : 'Nothing approved yet.'}
        </div>
      ) : rows.map((row) => {
        const expanded = open === row.id;
        const editable = expanded && isLive(row.state) && meta.can_approve && !!sheet;
        const openBlocks = row.blocking_unresolved || [];
        // Typed but not saved. The figures on screen are not the figures a
        // decision would act on, so no decision is offered until they agree.
        // Still worth knowing about — Save rebuilds the letter and an
        // approver about to APPROVE has to read the rebuilt one — but no
        // longer load-bearing: a decision carries these edits with it.
        const unsaved = editable && sheetPrint(sheet) !== sheetPrint(seedFrom(row));
        const unsavedWhy = 'Approving reads the letter, and yours has changed. '
          + 'Save, open the rebuilt letter, then approve.';
        const blockedReason = !meta.can_approve
          ? 'You are not a closeout approver.'
          : unsaved
          ? unsavedWhy
          : openBlocks.length
          ? 'Override each must-fix with a reason, or send it back.'
          : !row.viewed_by_me
          ? 'Open the letter and read it before approving.'
          : null;
        const net = editable ? netOf(sheet) : row.figures?.client_net;
        const moved = editable && Math.abs(net - (row.figures?.client_net ?? 0)) > 0.005;

        return (
          <div key={row.id} style={s.card}>
            <div style={s.cardHead} role="button" tabIndex={0}
                 aria-expanded={expanded}
                 aria-label={`${row.client_name}, ${row.state}`}
                 onClick={() => setOpen(expanded ? null : row.id)}
                 onKeyDown={(e) => {
                   if (e.key === 'Enter' || e.key === ' ') {
                     e.preventDefault();
                     setOpen(expanded ? null : row.id);
                   }
                 }}>
              <div>
                <div style={s.client}>
                  {row.client_name} {stateTag(row.state)}
                  {row.cleared_on_accept && row.state === 'changes_requested' && (
                    <span style={{ ...s.tag, color: '#304822', background: '#e4edcd',
                                   borderColor: '#9eb574' }}>
                      Signed off — waiting on the preparer
                    </span>
                  )}
                  {row.needs_decision && (
                    <span style={{ ...s.tag, color: '#8c3315', background: '#f8e3dc', borderColor: '#d39e8a' }}>
                      Needs a decision
                    </span>
                  )}
                  {(row.changes || []).length > 0 && (
                    <span style={{ ...s.tag, color: '#2c474f', background: '#e3edef', borderColor: '#adc2c7' }}>
                      {row.changes.length} change{row.changes.length === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
                <div style={s.muted}>
                  Prepared by {row.prepared_by_name || row.prepared_by} ·{' '}
                  {formatDateTime(row.created_at)}
                  {row.revision > 1 ? ` · v${row.revision}` : ''}
                </div>
              </div>
              <div style={s.netBox}>
                <div style={s.netLabel}>Net to client</div>
                {moved && (
                  <div style={s.netWas}>{formatCurrency(row.figures?.client_net)}</div>
                )}
                <div style={s.net}>{formatCurrency(net)}</div>
              </div>
            </div>

            {expanded && (
              <div style={s.body}>
                {editable && (
                  <div style={s.editBanner}>
                    <strong>You are editing {row.prepared_by_name || 'the preparer'}’s sheet.</strong>{' '}
                    Every change is recorded against your name and shown to them.
                    Saving rebuilds the letter, so you will need to open and read
                    it again before you can approve.
                  </div>
                )}

                {editable
                  ? <EditableSheet sheet={sheet} setSheet={setSheet}
                                   dirty={unsaved} marks={changeLookup(row)}
                                   onDiscard={() => setSheet(seedFrom(row))} />
                  : <ReadOnlySheet row={row} />}

                {(row.changes || []).length > 0 && (
                  <div style={s.changeBox}>
                    <div style={s.changeHead}>
                      {row.changed_by_name || row.revised_by_name || 'An approver'}
                      {' '}changed this sheet
                      {row.changed_by && row.changed_by === row.prepared_by
                        ? ' after you sent it back'
                        : ''}
                    </div>
                    <table style={s.table}>
                      <tbody>
                        {row.changes.map((c, i) => (
                          <tr key={`${c.field}:${i}`}>
                            <td>{c.label}</td>
                            <td style={s.was}>{c.was_text}</td>
                            <td style={s.now}>{c.now_text}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {(row.overrides || []).map((o, i) => (
                  <div key={`${o.code}:${i}`} style={s.overrideDone}>
                    <strong>Overridden by management.</strong> <em>{o.message}</em>
                    <div style={{ marginTop: 5 }}>“{o.reason}”</div>
                  </div>
                ))}

                {openBlocks.map((f, i) => (
                  <div key={`${f.code}:${i}`} style={s.blockBox}>
                    <div><strong>Must fix{meta.can_approve ? ' — or override it' : ''}.</strong>{' '}
                      {f.message}</div>
                    {meta.can_approve && isLive(row.state) && (
                      <>
                        <textarea style={{ ...s.textarea, marginTop: 8 }}
                                  value={overrides[f.code] || ''}
                                  onChange={(e) => setOverrides(
                                    (o) => ({ ...o, [f.code]: e.target.value }))}
                                  placeholder="Why this is going out anyway — required, and it goes on the record" />
                        <div style={s.muted}>
                          Saving with a reason here clears this must-fix. The reason goes
                          on the packet, into the audit trail and into the Slack thread
                          under your name.
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {(row.reconciliation?.warnings || []).length > 0 && (
                  <div style={s.warn}>
                    <strong>The preparer confirmed these:</strong>
                    <ul style={s.ul}>
                      {row.reconciliation.warnings.map((f, i) => (
                        <li key={`${f.code}:${i}`}>{f.message}</li>
                      ))}
                    </ul>
                    {row.acknowledgement_note && (
                      <div style={{ marginTop: 6 }}>
                        Their note: “{row.acknowledgement_note}”
                      </div>
                    )}
                  </div>
                )}
                {(row.reconciliation?.info || []).map((f, i) => (
                  <div key={`${f.code}:${i}`} style={s.note}>{f.message}</div>
                ))}
                {row.reconciliation?.clean && (
                  <div style={s.ok}>
                    Checked against Filevine — every lienholder and provider on
                    this case is accounted for.
                  </div>
                )}

                {(row.problems || []).map((p, i) => (
                  <div key={i} style={s.problemBox}>
                    <strong>{p.by_name || p.by} raised a problem</strong>{' '}
                    <span style={s.muted}>{formatDateTime(p.at)}</span>
                    <div style={{ marginTop: 4 }}>“{p.note}”</div>
                  </div>
                ))}

                <div style={s.btnRow}>
                  <button style={s.btn} onClick={() => download(row, false)}>
                    {row.viewed_by_me ? 'Open the letter again' : 'Open the letter'}
                  </button>
                  {row.state === 'approved' && (
                    <button style={s.primary} onClick={() => download(row, true)}>
                      Download the approved letter
                    </button>
                  )}
                  {row.state === 'approved' && meta.can_approve && (
                    <div style={{ width: '100%', marginTop: 8 }}>
                      <textarea style={s.textarea} value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Why it is being reopened — required" />
                      <div style={s.muted}>
                        The approved letter was already released. Reopening
                        stops it being downloaded again; it does not recall a
                        copy that has gone to the client.
                      </div>
                    </div>
                  )}
                  {editable && (
                    // Disabled while the sheet cannot build. The server
                    // refuses it anyway with the same message, but an
                    // approver should not have to press a button to be told
                    // what the figures in front of them already say.
                    <button style={s.save}
                            disabled={busy || sheetErrors(sheet).length > 0}
                            onClick={() => save(row)}>
                      Save changes &amp; rebuild the letter
                    </button>
                  )}
                  {isLive(row.state) && row.is_mine && (
                    <button style={s.btn} onClick={() => withdraw(row)} disabled={busy}>
                      Withdraw
                    </button>
                  )}
                  {/* Approved was terminal — a mistake found afterwards had
                      nowhere to go but a second packet built beside the
                      first. Reversing an approval is an approver's call and
                      needs a reason, and it does not recall a letter that
                      has already gone to the client. */}
                  {row.state === 'approved' && meta.can_approve && (
                    <button style={s.danger} onClick={() => reopen(row)}
                            disabled={busy}
                            title="Takes this back for a fix. Stops the approved letter being downloaded again; does not recall one already sent.">
                      Reopen for a fix
                    </button>
                  )}
                </div>

                {isLive(row.state) && (
                  <div style={s.decide}>
                    <textarea style={s.textarea} value={note}
                              onChange={(e) => setNote(e.target.value)}
                              placeholder="Note — required when sending it back" />
                    <div style={s.btnRow}>
                      {/* Not gated on saving. Sending a packet back is not
                          an approval, so there is no letter to have read —
                          the decision carries the edits and the server
                          stores them before it acts. */}
                      <button style={s.danger}
                              disabled={busy || !meta.can_approve}
                              onClick={() => decide(row, 'request_changes')}>
                        Request changes
                      </button>
                      {/* Only where there is something to accept. On an
                          untouched sheet this is just Approve with an extra
                          step bolted to it, and the server says so. */}
                      {(row.changes || []).length > 0 && (
                        <button style={{ ...s.btn, opacity: blockedReason ? 0.5 : 1,
                                         cursor: blockedReason ? 'not-allowed' : 'pointer' }}
                                disabled={busy || !!blockedReason}
                                title="Signs off on the sheet as you have left it. The preparer accepts your figures and the letter is released — no second round."
                                onClick={() => decide(row, 'approve_on_accept')}>
                          Approve with my changes
                        </button>
                      )}
                      <button style={{ ...s.primary, opacity: blockedReason ? 0.5 : 1,
                                       cursor: blockedReason ? 'not-allowed' : 'pointer' }}
                              disabled={busy || !!blockedReason}
                              onClick={() => decide(row, 'approve')}>
                        Approve
                      </button>
                    </div>
                    {blockedReason && <div style={s.muted}>{blockedReason}</div>}
                    {!blockedReason && row.is_mine && (
                      <div style={s.muted}>
                        You prepared this packet — approving it is recorded as a
                        self-approval.
                      </div>
                    )}
                  </div>
                )}

                {!isLive(row.state) && row.decided_by && (
                  <div style={s.muted}>
                    {row.state === 'approved' ? 'Approved' : 'Decided'} by {row.decided_by} ·{' '}
                    {formatDateTime(row.decided_at)}
                    {/* Only ever beside an approval. Packets sent back
                        before this was fixed still carry the flag, and the
                        phrase is untrue of every one of them. */}
                    {row.state === 'approved' && row.self_approved
                      ? ' · self-approved' : ''}
                    {row.decision_note ? ` — “${row.decision_note}”` : ''}
                  </div>
                )}

                {row.state === 'approved' && !meta.can_approve && (
                  <div style={s.decide}>
                    <textarea style={s.textarea} value={problem}
                              onChange={(e) => setProblem(e.target.value)}
                              placeholder="Something wrong with this packet?" />
                    <div style={s.btnRow}>
                      <button style={s.btn} disabled={busy || !problem.trim()}
                              onClick={() => raise(row)}>
                        Raise a problem with management
                      </button>
                    </div>
                    <div style={s.muted}>
                      This goes to every approver in the packet’s Slack thread. It does
                      not unapprove anything — the approval is management’s and only an
                      approver can move it.
                    </div>
                  </div>
                )}

                <details style={{ marginTop: 10 }}>
                  <summary style={s.summary}>Audit trail</summary>
                  <ul style={s.ul}>
                    {(row.history || []).map((h, i) => (
                      <li key={i} style={s.muted}>
                        {formatDateTime(h.timestamp)} — <strong>{h.action}</strong> — {h.actor}
                        {h.self_approved ? ' (self-approved)' : ''}
                        {h.escalated ? ' (sent up for a management decision)' : ''}
                        {h.detail ? ` — ${h.detail}` : ''}
                        {(h.changes || []).length
                          ? ` — ${h.changes.map((c) => `${c.label} ${c.was_text} → ${c.now_text}`).join(', ')}`
                          : ''}
                        {(h.overrides || []).map((o) => ` — overrode: ${o.reason}`).join('')}
                      </li>
                    ))}
                  </ul>
                  <div style={s.hash}>
                    Approved document SHA-256: {row.final_sha256 || '—'}
                  </div>
                </details>
              </div>
            )}
          </div>
        );
      })}

      {tab === 'approved' && (
        <label style={s.archiveToggle}>
          <input type="checkbox" checked={showArchived}
                 onChange={(e) => setShowArchived(e.target.checked)} />
          <span>Also show withdrawn and superseded packets</span>
        </label>
      )}
    </div>
  );
}

/** The distribution as submitted, for anyone who cannot edit it. */
function ReadOnlySheet({ row }) {
  // The breakdowns live on the submitted sheet rather than in `figures`,
  // which carries only the totals.
  const p = row.payload || {};
  // Redlined here too.
  //
  // This is the sheet everybody who is NOT a closeout approver sees — the
  // preparer, every time they open the Closeout tab — and the one an
  // approver sees once a packet is decided. It listed what had changed in
  // a box underneath and marked none of it on the figures themselves, so
  // the people most likely to be looking for a moved number were the ones
  // who could not see which number moved.
  const marks = changeLookup(row);
  const splits = (p.fee_splits || []).filter((r) => String(r[0] ?? '').trim());
  const advances = (p.advances || []).filter((r) => String(r[0] ?? '').trim());
  const firmShare = num(row.figures?.attorney_fees)
    - splits.reduce((t, r) => t + num(r[1]), 0);
  return (
    <table style={s.table}>
      <tbody>
        <tr><td>Total settlement</td>
            <td style={s.r}>{formatCurrency(row.figures?.settlement_amount)}
              <Was change={marks.field('settlement_amount')} /></td></tr>
        {(p.settlement_sources || [])
          .filter((r) => String(r[0] ?? '').trim())
          .map((r, i) => (
            <tr key={`src:${i}`}><td style={s.indent}>{r[0]}</td>
                <td style={s.r}>{formatCurrency(num(r[1]))}
                  <Was change={marks.row(r[0])} /></td></tr>
          ))}
        <tr><td>Attorney fees ({row.figures?.fee_label})</td>
            <td style={s.r}>({formatCurrency(row.figures?.attorney_fees)})
              <Was change={marks.field('attorney_fees')} /></td></tr>
        {splits.length > 0 && (
          <tr><td style={s.indent}>The May Firm</td>
              <td style={s.r}>{formatCurrency(firmShare)}</td></tr>
        )}
        {splits.map((r, i) => (
          <tr key={`split:${i}`}><td style={s.indent}>{r[0]}</td>
              <td style={s.r}>{formatCurrency(num(r[1]))}
                <Was change={marks.row(r[0])} /></td></tr>
        ))}
        <tr><td>Costs</td>
            <td style={s.r}>({formatCurrency(row.figures?.costs)})
              <Was change={marks.field('costs')} /></td></tr>
        <tr><td>Liens</td>
            <td style={s.r}>({formatCurrency(row.figures?.liens_total)})</td></tr>
        {(row.liens || []).map((lr, i) => (
          <tr key={`${lr[0]}:${i}`}>
            <td style={s.indent}>
              {lr[0]}
              {/* Shown exactly as the letter prints it, so somebody who
                  cannot edit the sheet can still see what the client will
                  read. Suppressed when it is not a reduction, same rule as
                  the builder. */}
              {lr[2] != null && Number(lr[2]) > Number(lr[1]) + 0.005 && (
                <span style={s.origNote}>
                  {' '}({lr[3] === 'asserted' ? 'originally asserted' : 'billed'}{' '}
                  {formatCurrency(lr[2])})
                </span>
              )}
            </td>
            <td style={s.r}>{formatCurrency(lr[1])}
              <Was change={marks.row(lr[0])} /></td>
          </tr>
        ))}
        {advances.length > 0 && (
          <tr><td>Client gross proceeds</td>
              <td style={s.r}>{formatCurrency(row.figures?.gross_proceeds)}</td></tr>
        )}
        {advances.map((r, i) => (
          <tr key={`adv:${i}`}><td style={s.indent}>{r[0]}</td>
              <td style={s.r}>({formatCurrency(num(r[1]))})
                <Was change={marks.row(r[0])} /></td></tr>
        ))}
        <tr style={s.totalRow}><td>Client final net proceeds</td>
            <td style={s.r}>{formatCurrency(row.figures?.client_net)}</td></tr>
      </tbody>
    </table>
  );
}

/**
 * The same distribution, live.
 *
 * Deliberately the whole sheet and not only the figures: the fixes that sent
 * packets back most often were a stale client address and a wrong RE line,
 * neither of which is a number.
 */
function EditableSheet({ sheet, setSheet, dirty, onDiscard, marks }) {
  const set = (k) => (e) => setSheet((f) => ({ ...f, [k]: e.target.value }));
  const setLien = (i, k) => (e) => setSheet((f) => ({
    ...f,
    liens: f.liens.map((l, j) => (j === i ? { ...l, [k]: e.target.value } : l)),
  }));
  const dropLien = (i) => setSheet((f) => ({
    ...f, liens: f.liens.filter((_, j) => j !== i),
  }));
  const addLien = () => setSheet((f) => ({
    ...f, liens: [...f.liens, { name: '', amount: '', original: '', original_kind: 'billed' }],
  }));

  // The three breakdowns edit identically — a name and an amount — so they
  // share one set of handlers rather than three copies that drift.
  const setRow = (key, i, k) => (e) => setSheet((f) => ({
    ...f,
    [key]: (f[key] || []).map((r, j) => (j === i ? { ...r, [k]: e.target.value } : r)),
  }));
  const dropRow = (key, i) => setSheet((f) => ({
    ...f, [key]: (f[key] || []).filter((_, j) => j !== i),
  }));
  const addRow = (key) => setSheet((f) => ({
    ...f, [key]: [...(f[key] || []), { name: '', amount: '' }],
  }));

  /** The editable rows of one breakdown, plus its add button. */
  const rowsFor = (key, { placeholder, addLabel, aria, bracket = false }) => (
    <>
      {(sheet[key] || []).map((r, i) => (
        <tr key={`${key}:${i}`}>
          <td style={s.indent}>
            <input style={s.lienName} value={r.name} placeholder={placeholder}
                   aria-label={`${aria} ${i + 1}, description`}
                   onChange={setRow(key, i, 'name')} />
          </td>
          <td style={s.r}>
            {bracket && '('}
            <input style={s.num} value={r.amount}
                   aria-label={`${aria} ${i + 1}, amount`}
                   onChange={setRow(key, i, 'amount')} />
            {bracket && ')'}
            <Was change={marks.row(r.name)} />
          </td>
          <td style={s.r}>
            <button style={s.xBtn} onClick={() => dropRow(key, i)}
                    aria-label={`Take ${r.name || 'this line'} off the sheet`}>×</button>
          </td>
        </tr>
      ))}
      <tr>
        <td colSpan={3} style={s.indent}>
          <button style={s.btn} onClick={() => addRow(key)}>{addLabel}</button>
        </td>
      </tr>
    </>
  );

  return (
    <div style={{ margin: '10px 0' }}>
      <table style={s.table}>
        <tbody>
          <tr>
            <td>Total settlement</td>
            <td style={s.r}>
              <input style={s.num} value={sheet.settlement_amount}
                     onChange={set('settlement_amount')} />
              <Was change={marks.field('settlement_amount')} />
            </td>
            <td />
          </tr>
          {/* Where the settlement came from, when it came from more than one
              place. The same money itemised, so these have to add up to the
              line above and the sheet says so when they do not. */}
          {rowsFor('settlement_sources', {
            placeholder: 'Third party / First party / Medical payments',
            addLabel: '+ Break the settlement down by source',
            aria: 'Settlement source',
          })}
          <tr>
            <td>Attorney fees ({sheet.fee_label})</td>
            <td style={s.r}>
              <input style={s.num} value={sheet.attorney_fees}
                     onChange={set('attorney_fees')} />
              <Was change={marks.field('attorney_fees')} />
            </td>
            <td />
          </tr>
          {/* The firm's own share is the remainder, computed here as it is
              on the letter. Editing the fee above moves it; nobody types
              it, so the sub-lines always sum to the fee. */}
          {breakdown(sheet, 'fee_splits').length > 0 && (
            <tr>
              <td style={s.indent}>The May Firm</td>
              <td style={s.r}>
                {formatCurrency(num(sheet.attorney_fees)
                  - breakdownTotal(sheet, 'fee_splits'))}
              </td>
              <td />
            </tr>
          )}
          {rowsFor('fee_splits', {
            placeholder: 'Co-counsel or the firm the case came from',
            addLabel: '+ Split the fee with another firm',
            aria: 'Fee split',
          })}
          <tr>
            <td>Costs</td>
            <td style={s.r}>
              <input style={s.num} value={sheet.costs} onChange={set('costs')} />
              <Was change={marks.field('costs')} />
            </td>
            <td />
          </tr>
          {/* The category the lien lines belong to.
              It was missing here and present everywhere else — the
              preparer's form, the read-only sheet and the letter all head
              these lines with LIENS and their total. Without it the
              providers sat indented directly under Costs, which is what
              they read as. Spotted on the 2026-11-02 deploy. */}
          <tr>
            <td>Liens</td>
            <td style={s.r}>{formatCurrency(liensOf(sheet))}</td>
            <td />
          </tr>
          {(sheet.liens || []).map((l, i) => (
            <tr key={i}>
              <td style={s.indent}>
                <input style={s.lienName} value={l.name} onChange={setLien(i, 'name')}
                       placeholder="Lienholder" />
                <div style={s.origRow}>
                  <select style={s.origKind} value={l.original_kind || 'billed'}
                          aria-label={`How ${l.name || 'this line'} claimed its original figure`}
                          onChange={setLien(i, 'original_kind')}>
                    <option value="billed">billed</option>
                    <option value="asserted">originally asserted</option>
                  </select>
                  <input style={s.origNum} value={l.original ?? ''}
                         placeholder="nothing on file"
                         aria-label={`What ${l.name || 'this line'} originally claimed`}
                         onChange={setLien(i, 'original')} />
                </div>
              </td>
              <td style={s.r}>
                <input style={s.num} value={l.amount} onChange={setLien(i, 'amount')} />
                <Was change={marks.row(l.name)} />
              </td>
              <td style={s.r}>
                <button style={s.xBtn} onClick={() => dropLien(i)}
                        aria-label={`Take ${l.name || 'this line'} off the sheet`}>×</button>
              </td>
            </tr>
          ))}
          <tr>
            <td colSpan={3}>
              <button style={s.btn} onClick={addLien}>+ Add a line</button>
            </td>
          </tr>
          {breakdown(sheet, 'advances').length > 0 && (
            <tr>
              <td>Client gross proceeds</td>
              <td style={s.r}>{formatCurrency(grossOf(sheet))}</td>
              <td />
            </tr>
          )}
          {rowsFor('advances', {
            placeholder: 'Pre-settlement loan / child support / firm advance',
            addLabel: '+ Add a loan, advance or child support',
            aria: 'Loan or advance',
            bracket: true,
          })}
          <tr style={s.totalRow}>
            <td>Client final net proceeds</td>
            <td style={s.r}>{formatCurrency(netOf(sheet))}</td>
            <td />
          </tr>
        </tbody>
      </table>

      {sheetErrors(sheet).map((msg, i) => (
        <div key={i} style={s.err}>{msg}</div>
      ))}
      {dirty && (
        <div style={s.warn}>
          <strong>You have changes on this sheet.</strong>{' '}
          Request changes sends them with the decision — you do not have to
          save first. Approving does: it rebuilds the letter, and you have to
          read the one you are signing.
          <button style={{ ...s.btn, marginLeft: 8 }} onClick={onDiscard}>
            Discard my edits
          </button>
        </div>
      )}

      <details style={{ marginTop: 6 }}>
        <summary style={s.summary}>Client details and letter wording</summary>
        <div style={s.grid}>
          {DETAIL_FIELDS.map(([k, label]) => (
            <div key={k}>
              <label style={s.label}>{label}</label>
              <input style={s.input} value={sheet[k] ?? ''} onChange={set(k)} />
            </div>
          ))}
        </div>
        <label style={s.label}>Address</label>
        <textarea style={{ ...s.textarea, minHeight: 48 }}
                  value={sheet.client_address_lines}
                  onChange={set('client_address_lines')} />
        <label style={s.label}>Released parties</label>
        <textarea style={{ ...s.textarea, minHeight: 40 }}
                  value={sheet.released_parties ?? ''}
                  onChange={set('released_parties')} />
      </details>
    </div>
  );
}

/*
 * Future Form values for this screen.
 *
 * Converted as a style table rather than as markup: Closeout is the approval
 * workflow, 900 lines of conditionals about who may do what and when, and
 * rewriting its JSX to chase a visual pass is how a permission check quietly
 * changes meaning. Every rule below is a colour, a radius or a size.
 *
 * The change-diff and editing chrome used indigo, which is not in the
 * palette. It is the approved slate instead — distinct from both the green
 * of a normal state and the red of something wrong, which is exactly the job
 * it was doing.
 */
const s = {
  wrap: { padding: 0 },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 14, flexWrap: 'wrap', gap: 10 },
  h2: { fontSize: 22, fontWeight: 400, letterSpacing: '-0.5px', color: '#1c2b22', margin: 0 },
  tabs: { display: 'flex', gap: 7 },
  tabBtn: { padding: '6px 12px', border: '1px solid #b4c0a5', background: 'transparent',
    color: '#4a5d40', borderRadius: 25, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' },
  tabOn: { background: '#dce7bd', color: '#2a421c', borderColor: '#859e56' },
  count: { display: 'inline-block', marginLeft: 6, padding: '1px 7px', borderRadius: 20,
    background: '#e1e8d4', color: '#34452a', fontSize: 11, fontWeight: 500 },
  countLoud: { background: '#f8e3dc', color: '#8c3315' },
  archiveToggle: { display: 'flex', gap: 7, alignItems: 'center', marginTop: 14,
    fontSize: 12, color: '#53624f', cursor: 'pointer' },
  card: { border: '1px solid #cbd2c0', borderRadius: 18, marginBottom: 12,
    background: 'linear-gradient(135deg,#fffef9,#f9faf3)', overflow: 'hidden' },
  cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 20px', cursor: 'pointer', gap: 12 },
  client: { fontSize: 15, fontWeight: 500, color: '#1c2b22', marginBottom: 3 },
  netBox: { textAlign: 'right' },
  netLabel: { fontSize: 9, color: '#53624f', textTransform: 'uppercase',
    letterSpacing: '0.17em' },
  net: { fontSize: 23, color: '#1c2b22', fontFamily: "Georgia, 'Times New Roman', serif",
    fontVariantNumeric: 'tabular-nums' },
  body: { padding: '4px 20px 20px', borderTop: '1px solid #e2e7d8' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12, margin: '10px 0' },
  r: { textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  indent: { paddingLeft: 18, color: '#53624f' },
  totalRow: { fontWeight: 500, borderTop: '1px solid #cbd2c0' },
  tag: { fontSize: 9, padding: '3px 8px', borderRadius: 24, marginLeft: 8,
    verticalAlign: 'middle', border: '1px solid transparent' },
  muted: { fontSize: 12, color: '#53624f', lineHeight: 1.5 },
  note: { background: '#eef1e4', color: '#3f4f3a', padding: '10px 13px',
    borderRadius: 10, fontSize: 12, marginBottom: 10, lineHeight: 1.5,
    border: '1px solid #cbd2c0' },
  warn: { background: '#f5e9cf', color: '#714712', padding: '10px 13px',
    borderRadius: 10, fontSize: 12, margin: '8px 0', lineHeight: 1.5,
    border: '1px solid #d5b87f' },
  ok: { background: '#e4edcd', color: '#304822', padding: '10px 13px',
    borderRadius: 10, fontSize: 12, margin: '8px 0', lineHeight: 1.5,
    border: '1px solid #9eb574' },
  err: { background: '#f8e3dc', color: '#8c3315', padding: '10px 13px',
    borderRadius: 10, fontSize: 12, marginBottom: 10, lineHeight: 1.5,
    border: '1px solid #d39e8a' },
  ul: { margin: '6px 0 0 0', paddingLeft: 18 },
  btnRow: { display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' },
  btn: { padding: '9px 13px', border: '1px solid #a4b295', background: '#fffef9',
    color: '#2d4226', borderRadius: 10, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' },
  primary: { padding: '9px 15px', border: '1px solid #8b9f3a',
    background: 'linear-gradient(120deg,#d0e475,#a4bf45)',
    color: '#142008', borderRadius: 10, fontSize: 11,
    fontFamily: 'inherit', cursor: 'pointer' },
  danger: { padding: '9px 13px', border: '1px solid #d39e8a', background: '#fffef9',
    color: '#8c3315', borderRadius: 10, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' },
  decide: { marginTop: 14, borderTop: '1px solid #e2e7d8', paddingTop: 14 },
  textarea: { width: '100%', boxSizing: 'border-box', padding: '10px 11px',
    border: '1px solid #a4b295', borderRadius: 10, fontSize: 13, color: '#21321f',
    fontFamily: 'inherit', minHeight: 54, resize: 'vertical', background: '#fffef9' },
  summary: { fontSize: 12, color: '#53624f', cursor: 'pointer' },
  hash: { fontSize: 10, color: '#6d7a66', marginTop: 6, wordBreak: 'break-all',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
  wasLine: { fontSize: 10.5, color: '#b91c1c', marginTop: 2, whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums' },
  netWas: { fontSize: 11, color: '#8c3315', textDecoration: 'line-through',
    fontVariantNumeric: 'tabular-nums' },
  editBanner: { background: '#e3edef', border: '1px solid #adc2c7', color: '#2c474f',
    padding: '11px 13px', borderRadius: 10, fontSize: 12.5, margin: '10px 0',
    lineHeight: 1.5 },
  changeBox: { border: '1px solid #adc2c7', borderRadius: 10, background: '#f7fbfb',
    margin: '10px 0', overflow: 'hidden' },
  changeHead: { background: '#e3edef', color: '#2c474f', fontWeight: 500,
    fontSize: 11, padding: '8px 11px', letterSpacing: '0.04em' },
  was: { textAlign: 'right', color: '#8c3315', textDecoration: 'line-through',
    whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  now: { textAlign: 'right', color: '#1c2b22', fontWeight: 500,
    whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  blockBox: { background: '#f8e3dc', color: '#8c3315', padding: '11px 13px',
    borderRadius: 10, fontSize: 12.5, margin: '8px 0', lineHeight: 1.5,
    border: '1px solid #d39e8a' },
  overrideDone: { border: '1px solid #d39e8a', borderRadius: 10, background: '#fffef9',
    color: '#1c2b22', padding: '11px 13px', fontSize: 12.5, margin: '8px 0',
    lineHeight: 1.5 },
  problemBox: { border: '1px solid #cbd2c0', borderLeft: '3px solid #8c3315',
    borderRadius: '0 10px 10px 0', background: '#faf9f2', padding: '10px 13px',
    fontSize: 12.5, margin: '8px 0', lineHeight: 1.5 },
  save: { padding: '9px 15px', border: '1px solid #3f6069', background: '#3f6069',
    color: '#f2f7f8', borderRadius: 10, fontSize: 11, fontWeight: 500,
    fontFamily: 'inherit', cursor: 'pointer' },
  num: { width: 120, textAlign: 'right', padding: '6px 9px', border: '1px solid #a4b295',
    borderRadius: 8, fontSize: 13, fontFamily: 'inherit', color: '#21321f',
    background: '#fffef9', fontVariantNumeric: 'tabular-nums' },
  lienName: { width: '100%', padding: '6px 9px', border: '1px solid #a4b295',
    borderRadius: 8, fontSize: 13, fontFamily: 'inherit', color: '#21321f',
    background: '#fffef9' },
  origNote: { color: '#2c474f', fontSize: 11.5 },
  origRow: { display: 'flex', gap: 5, alignItems: 'center', marginTop: 4 },
  origKind: { fontSize: 10.5, color: '#53624f', border: '1px solid #cbd2c0',
    borderRadius: 7, padding: '3px 5px', fontFamily: 'inherit', background: '#f6f8ef' },
  origNum: { width: 110, fontSize: 11.5, padding: '4px 7px', border: '1px solid #cbd2c0',
    borderRadius: 7, fontFamily: 'inherit', color: '#21321f', textAlign: 'right',
    background: '#fffef9', fontVariantNumeric: 'tabular-nums' },
  xBtn: { border: 'none', background: 'none', color: '#8c3315', fontSize: 16,
    cursor: 'pointer', padding: '0 4px', lineHeight: 1 },
  input: { width: '100%', boxSizing: 'border-box', padding: '7px 10px',
    border: '1px solid #a4b295', borderRadius: 8, fontSize: 13,
    fontFamily: 'inherit', color: '#21321f', background: '#fffef9' },
  label: { display: 'block', fontSize: 9, color: '#3d5035', textTransform: 'uppercase',
    letterSpacing: '0.17em', margin: '10px 0 4px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 8 },
};

export default CloseoutApprovals;
